<template>
  <div
    @mouseover="hovering = true"
    @mouseleave="hovering = false"  
  >
    <component
      :is="answerComponentName"
      :userFormId="userFormId"
      :question="question"
      :fieldType="fieldType"
      :listId="listId"
      :readonly="questionReadOnly"
      :isEmpty="isEmpty"
      :showLastUpdated="true"
      @update:listId="$emit('update:listId', arguments[0])"
      @fieldFocused="isFieldFocused = true"
      @fieldUnfocused="isFieldFocused = false"
    > 
      <div
        v-if="reviewEnabled && checkPermission('userform_answer_review', PERMISSIONS.CAN_EDIT)"
        style="margin-bottom: 10px;"
        :class="{ 'review-panel-hover': hovering }"
      >
        <v-menu
          v-model="answerHistory"
          :close-on-content-click="false"
          transition="slide-y-transition"
          right
        >
          <v-fade-transition slot="activator">
            <span 
              v-if="hovering || answerHistory"
              style="position: absolute; left: 0; "
            >
              <v-icon>mdi-history</v-icon> <v-icon small>mdi-chevron-down</v-icon>
            </span>
          </v-fade-transition>
          <AnswerHistoryReview
            :focused.sync="answerHistory"
            :userFormId="userFormId"
            :fieldTypeId="fieldType.id"
            :questionId="question.id"
            :listId="listId"         
          />
        </v-menu>
        <v-menu
          v-model="statusChange"
          offset-y
          nudge-left="95"
        >
          <v-fade-transition slot="activator">
            <span 
              v-if="hovering || statusChange || isFieldFocused"
              style="position: absolute; right: 100px; margin-right: 1em;"
            >
              <v-icon>mdi-flag</v-icon> <v-icon small>mdi-chevron-down</v-icon>
            </span>
          </v-fade-transition>
          <v-list>
            <v-list-tile 
              v-if="showNeedsReviewOption"
              @click="updateStatus('follow up')"
            >
              <v-list-tile-title>Needs Review</v-list-tile-title>
            </v-list-tile>
            <v-list-tile 
              v-if="showReviewedOption"
              @click="updateStatus('reviewed')"
            >
              <v-list-tile-title>Reviewed</v-list-tile-title>
            </v-list-tile>
            <v-list-tile 
              v-if="showClearOption"
              @click="updateStatus(null)"
            >
              <v-list-tile-title>Clear Status</v-list-tile-title>
            </v-list-tile>
          </v-list>
        </v-menu>
        <v-menu
          v-model="menu"
          :close-on-content-click="false"
          offset-x
          nudge-left="425"
          nudge-top="10"
        >
          <v-fade-transition slot="activator">
            <div
              v-if="showCommentText"
              style="position: absolute;right:-0px; margin-right: 1em;"
            >
              <div 
                v-if="count || commentsAdded"
              >
                <span>Comments <b>({{ count + commentsAdded }})</b></span>
              </div>
              <div
                v-else
              >
                Add Comment
              </div>
            </div>
          </v-fade-transition>
          <AnswerReview
            v-if="menu"
            :userFormId="userFormId"
            :fieldTypeId="fieldType.id"
            :questionId="question.id"
            :listId="listId"
            @update:listId="$emit('update:listId', arguments[0])"
            @comment="commentsAdded++"
          />
        </v-menu>
      </div>
    </component>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex'
import api from '@/api'
import TextAnswer from '@/components/global/atomic/TextAnswer'
import SsnAnswer from '@/components/global/atomic/SsnAnswer'
import EmailAnswer from '@/components/global/atomic/EmailAnswer'
import DateAnswer from '@/components/global/atomic/DateAnswer'
import HDateAnswer from '@/components/global/atomic/HDateAnswer'
import DropdownAnswer from '@/components/global/atomic/DropdownAnswer'
import RadioAnswer from '@/components/global/atomic/RadioAnswer'
import NameAnswer from '@/components/global/atomic/NameAnswer'
import NumberAnswer from '@/components/global/atomic/NumberAnswer'
import CurrencyAnswer from '@/components/global/atomic/CurrencyAnswer'
import LongTextAnswer from '@/components/global/atomic/LongTextAnswer'
import CheckBoxAnswer from '@/components/global/atomic/CheckBoxAnswer'
import FileAnswer from '@/components/global/atomic/FileAnswer'
import AltEsignAnswer from '@/components/global/atomic/AltEsignAnswer'
import RegexAnswer from '@/components/global/atomic/RegexAnswer'
import answerMixin from '@/components/global/atomic/answermixin'
import AnswerReview from '@/components/global/atomic/AnswerReview'
import AnswerHistoryReview from '@/components/global/atomic/AnswerHistoryReview'

export default {
  name: 'AnswerField',
  mixins: [answerMixin],
  props: [ 'userFormId', 'question', 'fieldType', 'listId', 'isEmpty', 'readOnly' ],
  data () {
    return {
      menu: false,
      hovering: false,
      statusChange: false,
      failed: false,
      isFieldFocused: false,
      commentsAdded: 0,
      answerHistory: false
    }
  },
  components: {
    TextAnswer,
    SsnAnswer,
    EmailAnswer,
    DateAnswer,
    HDateAnswer,
    DropdownAnswer,
    RadioAnswer,
    NameAnswer,
    CurrencyAnswer,
    LongTextAnswer,
    CheckBoxAnswer,
    NumberAnswer,
    AnswerReview,
    FileAnswer,
    AltEsignAnswer,
    RegexAnswer,
    AnswerHistoryReview
  },
  computed: {
    ...mapGetters('userform', [
      'isReadOnly',
      'reviewEnabled',
      'answer'
    ]),
    questionReadOnly () {
      return this.isReadOnly || !this.checkPermission('userform', this.PERMISSIONS.CAN_EDIT) && this.checkPermission('userform', this.PERMISSIONS.CAN_CONTACT_EDIT) && this.question.read_only
    },
    answerComponentName () {
      if (this.fieldType.atomic_type === 'text') {
        return 'TextAnswer'
      } else if (this.fieldType.atomic_type === 'ssn') {
        return 'SsnAnswer'
      } else if (this.fieldType.atomic_type === 'email') {
        return 'EmailAnswer'
      } else if (this.fieldType.atomic_type === 'date') {
        return 'DateAnswer'
      } else if (this.fieldType.atomic_type === 'harddate') {
        return 'HDateAnswer'
      } else if (this.fieldType.atomic_type === 'dropdown') {
        return 'DropdownAnswer'
      } else if (this.fieldType.atomic_type === 'radio') {
        return 'RadioAnswer'
      } else if (this.fieldType.atomic_type === 'name') {
        return 'NameAnswer'
      } else if (this.fieldType.atomic_type === 'currency') {
        return 'CurrencyAnswer'
      } else if (this.fieldType.atomic_type === 'number') {
        return 'NumberAnswer'
      } else if (this.fieldType.atomic_type === 'textarea') {
        return 'LongTextAnswer'
      } else if (this.fieldType.atomic_type === 'checkbox') {
        return 'CheckBoxAnswer'
      } else if (this.fieldType.atomic_type === 'file') {
        return 'FileAnswer'
      } else if (this.fieldType.atomic_type === 'altesign') {
        return 'AltEsignAnswer'
      } else if (this.fieldType.atomic_type === 'regex') {
        return 'RegexAnswer'
      }
      return false;
    },
    count () {
      var answer = this.answer(this.question.id, this.fieldType.id, this.listId ? this.listId.id : null)
      if (answer)
        return answer.count
      else
        return 0
    },
    status () {
      const answer = this.answer(this.question.id, this.fieldType.id, this.listId ? this.listId.id : null)
      return (answer) ? answer.status : null
    },
    showClearOption () {
      if (this.status !== null) return true
      return false
    },
    showNeedsReviewOption () {
      if (this.status === null || this.status === 'reviewed') return true
      return false
    },
    showReviewedOption () {
      if (this.status === null || this.status === 'follow up') return true
      return false
    },
    showCommentText () {
      return this.hovering || this.menu || this.count || this.commentsAdded
    },
    showAnswerHistory () {
      return this.hovering
    }
  },
  methods: {
    ...mapActions('userform', [ 'updateAnswerStatus' ]),
    ...mapMutations('userform', [ 'setSaving', 'unsetSaving' ]),
    updateStatus (newStatus) {
      this.failed = false
      const saveAction = this.updateAnswerStatus({
        userFormId: this.userFormId,
        questionId: this.question.id,
        fieldTypeId: this.fieldType.id,
        listId: this.listId ? this.listId.id : null,
        clientId: this.listId ? this.listId.uuid : null,
        status: newStatus
      }).catch(() => {
        this.failed = true
        this.unsetSaving(saveAction)
      }).then(response => {
        if (this.listId && this.listId.uuid && response.answerlist_id) {
          this.$emit('update:listId', {id: response.answerlist_id, uuid: this.listId.uuid})
        }
        this.unsetSaving(saveAction)
      })
      this.setSaving(saveAction)
    }
  }
}
</script>

<style>
  .input-group {
    position: relative;
    width: 100%;
  }

.input-group-btn {
  position: absolute;
  right:-25px;
  top: -10px;
  z-index: 999;
}

.review-panel-hover {
  background-color: rgba(0, 0, 0, 0.04);
  border-radius: 4px;
  position: relative;
  transition: background-color 1s, border-radius 0.2s, box-shadow 1s;
}
</style>
